import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { subscribeToRoom } from '@db/roomService';
import { submitAnswer } from '@db/submissionService';
import { getCurrentUid, ensureAnonymousAuth } from '@db/firebase';
import type { MediaUploadConfig } from '@types-local/quiz.types';
import type { Room } from '@types-local/room.types';

/** Reads a File into a data URL for upload. */
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function AnswerMedia(): React.ReactElement {
  const [room, setRoom] = useState<Room | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const studentName = sessionStorage.getItem('gh_student_name') ?? 'Student';
  const roomCode = sessionStorage.getItem('gh_room_code') ?? '';
  const classId = sessionStorage.getItem('gh_class_id') ?? '';

  useEffect(() => {
    ensureAnonymousAuth();
    if (!roomCode) return;
    return subscribeToRoom(roomCode, setRoom);
  }, [roomCode]);

  const config = room?.activeConfig?.type === 'media_upload'
    ? (room.activeConfig as MediaUploadConfig) : null;

  // Reset for each new activity so students flow through the session.
  useEffect(() => {
    setPreview('');
    setFile(null);
    setError('');
    setSubmitted(false);
  }, [config?.prompt]);

  const handlePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError('');
    const maxBytes = (config?.maxFileSizeMb ?? 10) * 1024 * 1024;
    if (f.size > maxBytes) {
      setError(`That photo is too large (max ${config?.maxFileSizeMb ?? 10}MB). Try a smaller one.`);
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async () => {
    if (!file || !room?.activeSlideId) return;
    setSubmitting(true);
    setError('');
    try {
      const dataUrl = await readAsDataUrl(file);
      const uid = getCurrentUid() ?? 'anon';
      await submitAnswer(roomCode, uid, studentName, room.activeSlideId, {
        type: 'media_upload',
        fileUrl: dataUrl,        // uploaded to Storage inside submitAnswer
        fileName: file.name,
        mimeType: file.type || 'image/jpeg',
      }, classId);
      setSubmitted(true);
    } catch (err) {
      console.error('[Media] Submit failed:', err);
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div className="space-y-3"><div className="text-5xl">📸</div><h2 className="text-xl font-bold text-slate-900">Waiting for activity…</h2></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div className="space-y-4">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-6xl">🎉</motion.div>
          <h2 className="text-xl font-bold text-slate-900">Photo Submitted!</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col p-4 max-w-lg mx-auto">
      <div className="glass rounded-3xl p-5 mb-4">
        <p className="text-xs text-blue-500 font-semibold mb-2">📸 Media Upload</p>
        <h2 className="text-base font-bold text-slate-900">{config.prompt}</h2>
      </div>

      {/* Hidden input; `capture` hints mobile browsers to open the camera. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handlePick}
      />

      {preview ? (
        <button onClick={() => fileInputRef.current?.click()}
          className="rounded-2xl overflow-hidden border-2 border-blue-500/40 mb-4">
          <img src={preview} alt="Your photo" className="w-full max-h-80 object-contain bg-black/5" />
        </button>
      ) : (
        <button onClick={() => fileInputRef.current?.click()}
          className="flex flex-col items-center justify-center gap-2 w-full py-16 rounded-2xl border-2 border-dashed border-blue-500/40 bg-blue-500/5 text-blue-600 mb-4">
          <span className="text-4xl">📷</span>
          <span className="text-sm font-semibold">Tap to take or choose a photo</span>
        </button>
      )}

      {error && (
        <p className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 mb-3">{error}</p>
      )}

      <motion.button
        onClick={handleSubmit}
        disabled={!file || submitting}
        whileTap={{ scale: 0.97 }}
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold text-base disabled:opacity-60"
      >
        {submitting ? 'Uploading…' : '🚀 Submit Photo'}
      </motion.button>
    </div>
  );
}
